/*--------------------------------------------------------------------------------------------
Program     :  TaskDialog.js
Description :  Main Page for Map TaskDialog
Date        :  15/01/2025
Created By  :  Jelbin
---------------------------------------------------------------------------------------------
TicketNo         Author          Date           ChangeDesc
---------------------------------------------------------------------------------------------
15012025       - Jelbin     -  15/01/2025       Created
---------------------------------------------------------------------------------------------*/
import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import { Table, TableBody, TableCell, TableRow, Box, IconButton, Typography, FormLabel } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CircularProgress from '@mui/material/CircularProgress';
import ParticipantsDialog from './ParticipantsDialog'
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const TaskDialog = ({
  // Dialog states
  dlgopen,
  startsave,
  // Dialog data
  messageData,
  reassignMapData,
  travelerName,
  selectedUKey,
  POIExist,
  selectedChooseData,
  deletePOI,
  note,
  noteData,
  editNote,
  description,
  noteLocalSave,
  tableUniqueValue,
  agentColor,
  // Context data
  combinedData,
  selectedtraveler,
  poiData,
  editablefield,
  chooseOptions,
  subformName,
  dataModified,

  // Event handlers
  handleDlgClose,
  handleUserChange,
  handlePOIUKey,
  handledeletePOI,
  handleDataReassign,
  handleChooseData,
  handleNote,
  handleNoteEdit,
  handleNoteView,
  setEditorState,
  imageUploadCallback,
  //input for ParticipantsDialog.js
  selectedtask,
  selectedagentindexval,
  participantsData,
  tableUniqueName,
  tskTravelTimeField,
  mapVisibilityDate,
  ZDATERANGE,
  durationFields,
  fielduserstartTime,
  startTimeField,
  endTimeField,
  fixschedulefield,
  selectedSourceMapname
}) => {
  // POI View handler for dialog
  const handlePOIview = (
    !Array.isArray(reassignMapData?.pointsOfInterest) ? (
      // If pointsOfInterest is not an array, render the Select dropdown for POIs
      <FormControl fullWidth style={{ marginTop: '16px' }}>
        <InputLabel id="user-select-label">Select POI</InputLabel>
        <Select
          labelId="user-select-label"
          id="user-select"
          value={selectedUKey}
          label="Select POI"
          onChange={handlePOIUKey}
        >
          {poiData.map((mapdata) => (
            <MenuItem key={mapdata.UNKdata} value={mapdata.UNKdata}>
              {mapdata.UNKdata}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : null
  );

  return (
    <>
      {/* Task Reassignment Dialog */}
      <Dialog open={dlgopen} PaperProps={{
        sx: {
          border: `2px solid ${agentColor}`,
          borderRadius: 5,
        },
      }}>
        <DialogTitle>Change Details</DialogTitle>
        <DialogContent>
          {messageData && Object.keys(messageData).length > 0 ? (
            Object.entries(messageData).map(([key, value]) => (
              <div key={key}>
                <strong>{key}:</strong> {value}
              </div>
            ))
          ) : null}
          {/*particpnt*/}
          {/*Display the task participants name*/}
          <hr style={{ margin: '12px 0', borderTop: '1px solid #e5e7eb' }} />
          <ParticipantsDialog
            selectedtask={selectedtask}
            selectedagentindexval={selectedagentindexval}
            participantsData={participantsData}
            tableUniqueName={tableUniqueName}
            tskTravelTimeField={tskTravelTimeField}
            selectedtraveler={selectedtraveler}
            mapVisibilityDate={mapVisibilityDate}
            ZDATERANGE={ZDATERANGE}
            durationFieldsY={durationFields?.[0]?.NAME || ""}
            fielduserstartTime={fielduserstartTime}
            startTimeField={startTimeField}
            endTimeField={endTimeField}
            fixschedulefield={fixschedulefield}
            handleDataReassign={handleDataReassign}
            selectedSourceMapname={selectedSourceMapname}
          />
          {POIExist ? (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell><b>POI</b></TableCell>
                  <TableCell><b>DELETE</b></TableCell>
                </TableRow>
                {reassignMapData?.pointsOfInterest && reassignMapData.pointsOfInterest.map((poi, index) => (
                  <TableRow key={index}>
                    <TableCell>{poi.UNKdata}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => handledeletePOI(index)}
                        style={{ cursor: 'pointer', background: 'none', border: 'none' }}
                      >
                        <ClearIcon fontSize="small" color="error" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {/* User Selection Dropdown */}
          <FormControl fullWidth style={{ marginTop: '16px' }}>
            <InputLabel id="user-select-label">Select User</InputLabel>
            <Select
              labelId="user-select-label"
              id="user-select"
              value={travelerName}
              label="Select User"
            >
              {combinedData.map((agent, index) => (
                <MenuItem
                  key={index}
                  value={agent[selectedtraveler]}
                  data-index={index}
                  onClick={() => handleUserChange(agent[selectedtraveler], index)}
                >
                  {agent[selectedtraveler]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* POI Selection Dropdown (if no POI is already selected) */}
          {!Array.isArray(reassignMapData?.pointsOfInterest) && (
            <FormControl fullWidth style={{ marginTop: '16px' }}>
              <InputLabel id="poi-select-label">Select POI</InputLabel>
              <Select
                labelId="poi-select-label"
                id="poi-select"
                value={selectedUKey}
                label="Select POI"
                onChange={handlePOIUKey}
              >
                {poiData.map((mapdata) => (
                  <MenuItem
                    key={mapdata.UNKdata}
                    value={mapdata.UNKdata}
                  >
                    {mapdata.UNKdata}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Editable Fields */}
          {editablefield.length > 0 && editablefield.map((field, index) => (
            <FormControl fullWidth style={{ marginTop: '16px' }} key={index}>
              <InputLabel shrink>{field.TITLE}</InputLabel>
              {chooseOptions[field.NAME] && chooseOptions[field.NAME].length !== 0 ? (
                <Select
                  value={selectedChooseData[field.NAME] || ""}
                  onChange={(e) => handleChooseData(e, field.NAME)}
                  label={field.TITLE}
                  sx={{ padding: 0, width: '100%' }}
                >
                  {/* Show default value from selectedtask if exists */}
                  {/* field.NAME = STATDES*/}
                  {selectedtask && selectedtask.hasOwnProperty(field.NAME) && selectedtask[field.NAME] && (
                    <MenuItem value={selectedtask[field.NAME]}>
                      {selectedtask[field.NAME]}
                    </MenuItem>
                  )}
                  {/*Display the new value*/}
                  {chooseOptions[field.NAME].map((option, optionIndex) => (
                    <MenuItem key={optionIndex} value={option.string1}>
                      {option.string1}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  value={selectedChooseData[field.NAME] || ''}
                  onChange={(e) => handleChooseData(e, field.NAME)}
                  sx={{ padding: 0, width: '100%' }}
                />
              )}
            </FormControl>
          ))}

          {/* Notes Section */}
          {subformName !== "" && (
            <>
              {!editNote ? (
                <Box sx={{ position: "relative", width: "100%" }}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel sx={{ fontSize: "14px", color: "gray", mb: 0.5 }}>Note</FormLabel>
                    <Box
                      sx={{
                        border: "1px solid gray",
                        borderRadius: "8px",
                        padding: "12px",
                        minHeight: "80px",
                        position: "relative",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Typography dangerouslySetInnerHTML={{ __html: noteData }} />
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          color: "gray",
                        }}
                        onClick={handleNoteEdit}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </FormControl>
                </Box>
              ) : (
                <Box sx={{ position: "relative", width: "100%" }}>
                  <TextField
                    id="outlined-multiline-static"
                    label="Note"
                    multiline
                    rows={4}
                    fullWidth
                    style={{ marginTop: '16px' }}
                    value={note}
                    onChange={(e) => handleNote(e.target.value)}
                  />
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: "gray",
                    }}
                    onClick={handleNoteView}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          {!startsave ? (
            <>
              <Button
                onClick={() => handleDataReassign()}
                disabled={!dataModified}
              >
                Confirm
              </Button>
              <Button onClick={handleDlgClose} disabled={deletePOI}>
                Cancel
              </Button>
            </>
          ) : (
            <div>
              <CircularProgress size={25} />&nbsp;Processing data reassignment...
            </div>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskDialog;